// src/pages/ManageDepartments.jsx - COMPLETE WITH SANITIZATION
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  listDepartments,
  addDepartment,
  deleteDepartment,
} from "../../Redux/actions/adminActions/departmentActions.js";
import Loading from "../../components/Loading.jsx";
import Error from "../../components/Error.jsx";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSanitizedInput } from "../../hooks/useSanitizedInput.js"; // ✅ Add import
import { Plus, Search, Trash2, Building2, X } from "lucide-react";

const ManageDepartments = () => {
  // ✅ Replace useState with sanitized hook
  const [deptName, handleDeptNameChange, resetDeptName, setDeptName] = useSanitizedInput("", "alphanumeric");
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);

  const dispatch = useDispatch();

  const { loading, error, departments, successAdd, successDelete } =
    useSelector((state) => state.departments);

  useEffect(() => {
    dispatch(listDepartments());
  }, [dispatch]);

  useEffect(() => {
    if (successAdd) {
      toast.success("✅ Department added successfully!");
      dispatch(listDepartments());
      setShowModal(false);
    }
    if (successDelete) {
      toast.info("🗑️ Department deleted successfully!");
      dispatch(listDepartments());
    }
    if (error) {
      toast.error(error);
    }
  }, [successAdd, successDelete, error, dispatch]);

  // ✅ Update submit handler with validation
  const submitHandler = (e) => {
    e.preventDefault();
    
    // ✅ Validate length
    if (deptName.trim().length < 2) {
      toast.warning("⚠️ Department name is too short (min 2 characters)");
      return;
    }
    
    if (deptName.length > 100) {
      toast.warning("⚠️ Department name is too long (max 100 characters)");
      return;
    }
    
    if (deptName.trim()) {
      dispatch(addDepartment(deptName));
      resetDeptName(); // ✅ Use hook's reset
    } else {
      toast.warning("⚠️ Please enter a department name.");
    }
  };

  const deleteHandler = (id) => {
    if (window.confirm("Are you sure you want to delete this department?")) {
      dispatch(deleteDepartment(id));
    }
  };

  const filteredDepartments = departments?.filter((dept) =>
    dept.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div style={{ background: "#f8f9fa", minHeight: "100vh", padding: "2rem" }}>
      <div className="container-fluid">
        {/* Header Section */}
        <div className="row mb-4">
          <div className="col-12">
            <div
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                padding: "2rem",
                borderRadius: "16px",
                color: "white",
                boxShadow: "0 8px 24px rgba(102, 126, 234, 0.25)",
              }}
            >
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div>
                  <h2 className="mb-2 fw-bold d-flex align-items-center">
                    <Building2 size={32} className="me-3" />
                    Department Management
                  </h2>
                  <p className="mb-0" style={{ opacity: 0.9 }}>
                    Manage hospital departments and their information
                  </p>
                </div>
                <button
                  className="btn btn-light btn-lg d-flex align-items-center"
                  onClick={() => setShowModal(true)}
                  style={{
                    borderRadius: "12px",
                    padding: "0.75rem 2rem",
                    fontWeight: "600",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  }}
                >
                  <Plus size={20} className="me-2" />
                  Add Department
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="row mb-4">
          <div className="col-md-4">
            <div
              className="card"
              style={{
                borderRadius: "12px",
                border: "none",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
              }}
            >
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="mb-1" style={{ opacity: 0.9, fontSize: "0.9rem" }}>
                      Total Departments
                    </p>
                    <h2 className="mb-0 fw-bold">{departments?.length || 0}</h2>
                  </div>
                  <div
                    style={{
                      background: "rgba(255,255,255,0.2)",
                      borderRadius: "12px",
                      padding: "1rem",
                    }}
                  >
                    <Building2 size={32} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div
              className="card"
              style={{
                borderRadius: "12px",
                border: "none",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                color: "white",
              }}
            >
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="mb-1" style={{ opacity: 0.9, fontSize: "0.9rem" }}>
                      Filtered Results
                    </p>
                    <h2 className="mb-0 fw-bold">{filteredDepartments?.length || 0}</h2>
                  </div>
                  <div
                    style={{
                      background: "rgba(255,255,255,0.2)",
                      borderRadius: "12px",
                      padding: "1rem",
                    }}
                  >
                    <Search size={32} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div
              className="card"
              style={{
                borderRadius: "12px",
                border: "none",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                color: "white",
              }}
            >
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="mb-1" style={{ opacity: 0.9, fontSize: "0.9rem" }}>
                      Active Status
                    </p>
                    <h2 className="mb-0 fw-bold">Online</h2>
                  </div>
                  <div
                    style={{
                      background: "rgba(255,255,255,0.2)",
                      borderRadius: "12px",
                      padding: "1rem",
                    }}
                  >
                    <div
                      style={{
                        width: "32px",
                        height: "32px",
                        background: "#4ade80",
                        borderRadius: "50%",
                        animation: "pulse 2s infinite",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="row mb-4">
          <div className="col-12">
            <div
              className="card"
              style={{
                borderRadius: "12px",
                border: "none",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              }}
            >
              <div className="card-body p-4">
                <div className="position-relative">
                  <Search
                    size={20}
                    style={{
                      position: "absolute",
                      left: "1rem",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#6c757d",
                    }}
                  />
                  <input
                    type="text"
                    placeholder="Search departments by name..."
                    className="form-control form-control-lg"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      paddingLeft: "3rem",
                      borderRadius: "10px",
                      border: "2px solid #e9ecef",
                      fontSize: "1rem",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Table Card */}
        <div className="row">
          <div className="col-12">
            <div
              className="card"
              style={{
                borderRadius: "16px",
                border: "none",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              }}
            >
              <div className="card-body p-0">
                {loading && (
                  <div className="text-center py-5">
                    <Loading />
                  </div>
                )}
                {error && (
                  <div className="p-4">
                    <Error message={error} />
                  </div>
                )}

                {!loading && !error && (
                  <div className="table-responsive" style={{ maxHeight: "600px", overflowY: "auto" }}>
                    <table className="table table-hover mb-0">
                      <thead
                        style={{
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          color: "white",
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                        }}
                      >
                        <tr>
                          <th
                            style={{
                              padding: "1.25rem 1.5rem",
                              fontWeight: 600,
                              fontSize: "0.95rem",
                              borderBottom: "none",
                            }}
                          >
                            #
                          </th>
                          <th
                            style={{
                              padding: "1.25rem 1.5rem",
                              fontWeight: 600,
                              fontSize: "0.95rem",
                              borderBottom: "none",
                            }}
                          >
                            Department Name
                          </th>
                          <th
                            style={{
                              padding: "1.25rem 1.5rem",
                              fontWeight: 600,
                              fontSize: "0.95rem",
                              textAlign: "center",
                              borderBottom: "none",
                            }}
                          >
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDepartments && filteredDepartments.length > 0 ? (
                          filteredDepartments.map((dept, index) => (
                            <tr
                              key={dept._id}
                              style={{
                                borderBottom: "1px solid #f0f0f0",
                                transition: "background 0.2s",
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.background = "#f8f9fa")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.background = "transparent")
                              }
                            >
                              <td style={{ padding: "1.25rem 1.5rem", fontWeight: 500 }}>
                                <div
                                  style={{
                                    width: "36px",
                                    height: "36px",
                                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                    borderRadius: "8px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "white",
                                    fontWeight: "600",
                                  }}
                                >
                                  {index + 1}
                                </div>
                              </td>
                              <td style={{ padding: "1.25rem 1.5rem" }}>
                                <div className="d-flex align-items-center">
                                  <div
                                    style={{
                                      background: "#e3f2fd",
                                      borderRadius: "10px",
                                      padding: "0.6rem",
                                      marginRight: "1rem",
                                    }}
                                  >
                                    <Building2 size={20} color="#667eea" />
                                  </div>
                                  <span className="fw-semibold" style={{ fontSize: "1rem" }}>
                                    {dept.name}
                                  </span>
                                </div>
                              </td>
                              <td style={{ padding: "1.25rem 1.5rem", textAlign: "center" }}>
                                <button
                                  onClick={() => deleteHandler(dept._id)}
                                  className="btn btn-outline-danger btn-sm d-inline-flex align-items-center"
                                  style={{
                                    borderRadius: "8px",
                                    padding: "0.5rem 1.25rem",
                                    fontWeight: "500",
                                  }}
                                >
                                  <Trash2 size={16} className="me-2" />
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan="3"
                              className="text-center py-5"
                              style={{ color: "#6c757d" }}
                            >
                              <Building2 size={64} color="#dee2e6" className="mb-3" />
                              <p className="mb-0 fw-semibold" style={{ fontSize: "1.1rem" }}>
                                No departments found
                              </p>
                              <p className="text-muted small">
                                {searchTerm
                                  ? "Try adjusting your search criteria"
                                  : "Click 'Add Department' to create your first department"}
                              </p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modern Bootstrap Modal */}
      {showModal && (
        <div
          className="modal fade show d-block"
          style={{ background: "rgba(0,0,0,0.5)" }}
          onClick={() => setShowModal(false)}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="modal-content"
              style={{ borderRadius: "16px", border: "none", overflow: "hidden" }}
            >
              <div
                className="modal-header"
                style={{
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                  border: "none",
                  padding: "1.5rem 2rem",
                }}
              >
                <h5 className="modal-title fw-bold d-flex align-items-center">
                  <Plus size={24} className="me-2" />
                  Add New Department
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowModal(false)}
                  style={{ opacity: 1 }}
                />
              </div>
              <form onSubmit={submitHandler}>
                <div className="modal-body p-4">
                  <div className="mb-3">
                    <label className="form-label fw-semibold" style={{ color: "#495057" }}>
                      Department Name
                    </label>
                    {/* ✅ Update input to use sanitized handler */}
                    <input
                      type="text"
                      placeholder="Enter department name (e.g., Cardiology)"
                      value={deptName}
                      onChange={handleDeptNameChange} // ✅ Use sanitized handler
                      className="form-control form-control-lg"
                      maxLength={100} // ✅ Add max length
                      style={{
                        borderRadius: "10px",
                        border: "2px solid #e9ecef",
                        padding: "0.75rem 1rem",
                      }}
                      autoFocus
                    />
                  </div>
                  <div
                    className="alert alert-info"
                    style={{ borderRadius: "10px", border: "none" }}
                  >
                    <small>
                      <strong>Tip:</strong> Enter a clear and descriptive name for the
                      department
                    </small>
                  </div>
                </div>
                <div
                  className="modal-footer"
                  style={{ border: "none", padding: "1rem 2rem 1.5rem" }}
                >
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowModal(false)}
                    style={{ borderRadius: "10px", padding: "0.6rem 1.5rem", fontWeight: "500" }}
                  >
                    <X size={18} className="me-2" />
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    style={{
                      borderRadius: "10px",
                      padding: "0.6rem 2rem",
                      fontWeight: "600",
                      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      border: "none",
                    }}
                  >
                    <Plus size={18} className="me-2" />
                    Save Department
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="bottom-right"
        autoClose={2500}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        theme="colored"
      />

      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.7;
            }
          }
        `}
      </style>
    </div>
  );
};

export default ManageDepartments;